import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { LoginModalProvider, useLoginModal } from "@/context/LoginModalContext";
import Layout from "@/components/Layout";
import LoginModal from "@/components/LoginModal";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import Tours from "@/pages/Tours";
import TourDetail from "@/pages/TourDetail";
import BookingPage from "@/pages/BookingPage";
import UserBookingDetail from "@/pages/UserBookingDetail";
import AdminBookingDetail from "@/pages/admin/AdminBookingDetail";
import Destinations from "@/pages/Destinations";
import DestinationDetail from "@/pages/DestinationDetail";
import Gallery from "@/pages/Gallery";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Enquiry from "@/pages/Enquiry";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Register from "@/pages/Register";
import UserDashboard from "@/pages/UserDashboard";
import NotFound from "@/pages/NotFound";

import Dashboard from "@/pages/admin/Dashboard";
import AdminTours from "@/pages/admin/AdminTours";
import AdminTourForm from "@/pages/admin/AdminTourForm";
import AdminDestinations from "@/pages/admin/AdminDestinations";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminUsers from "@/pages/admin/AdminUsers";

const withLayout = (el) => <Layout>{el}</Layout>;
const admin = (el) => <ProtectedRoute adminOnly>{el}</ProtectedRoute>;

function OpenLoginRedirect() {
  const { openLogin } = useLoginModal();
  useEffect(() => { openLogin(); }, [openLogin]);
  return <Navigate to="/" replace />;
}

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-center" richColors closeButton />
          <LoginModalProvider>
          <Routes>
            <Route path="/" element={withLayout(<Home />)} />
            <Route path="/tours" element={withLayout(<Tours />)} />
            <Route path="/tour/:slug" element={withLayout(<TourDetail />)} />
            <Route path="/book/:slug" element={withLayout(<BookingPage />)} />
            <Route path="/destinations" element={withLayout(<Destinations />)} />
            <Route path="/destination/:slug" element={withLayout(<DestinationDetail />)} />
            <Route path="/gallery" element={withLayout(<Gallery />)} />
            <Route path="/blog" element={withLayout(<Blog />)} />
            <Route path="/blog/:slug" element={withLayout(<BlogPost />)} />
            <Route path="/enquiry" element={withLayout(<Enquiry />)} />
            <Route path="/privacy-policy" element={withLayout(<Privacy />)} />
            <Route path="/terms" element={withLayout(<Terms />)} />
            <Route path="/login" element={<OpenLoginRedirect />} />
            <Route path="/admin/login" element={<OpenLoginRedirect />} />
            <Route path="/register" element={<Register />} />
            <Route path="/account" element={<ProtectedRoute>{withLayout(<UserDashboard />)}</ProtectedRoute>} />
            <Route path="/account/bookings/:id" element={<ProtectedRoute>{withLayout(<UserBookingDetail />)}</ProtectedRoute>} />

            <Route path="/admin" element={admin(<Dashboard />)} />
            <Route path="/admin/tours" element={admin(<AdminTours />)} />
            <Route path="/admin/tours/new" element={admin(<AdminTourForm />)} />
            <Route path="/admin/tours/edit/:id" element={admin(<AdminTourForm />)} />
            <Route path="/admin/destinations" element={admin(<AdminDestinations />)} />
            <Route path="/admin/gallery" element={admin(<AdminGallery />)} />
            <Route path="/admin/testimonials" element={admin(<AdminTestimonials />)} />
            <Route path="/admin/reviews" element={admin(<AdminReviews />)} />
            <Route path="/admin/enquiries" element={admin(<AdminEnquiries />)} />
            <Route path="/admin/bookings" element={admin(<AdminBookings />)} />
            <Route path="/admin/bookings/:id" element={admin(<AdminBookingDetail />)} />
            <Route path="/admin/payments" element={admin(<AdminPayments />)} />
            <Route path="/admin/blog" element={admin(<AdminBlog />)} />
            <Route path="/admin/users" element={admin(<AdminUsers />)} />
            <Route path="/admin/settings" element={admin(<AdminSettings />)} />

            <Route path="*" element={withLayout(<NotFound />)} />
          </Routes>
          <LoginModal />
          </LoginModalProvider>
        </BrowserRouter>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
